import { ArrowLeft, FilePlus2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { FilterField } from '@/components/filters/FilterBar'
import { MonthPicker } from '@/components/filters/MonthPicker'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCubeMeta } from '@/hooks/use-cube-meta'
import { cubeApi } from '@/lib/cube'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { periodLabel, resolveRelativePeriod } from '@/lib/period'
import { ApiError } from '@/lib/problem'
import { reportDocumentSchema, type ReportDocument } from '@/lib/report-document'
import { useBiReport } from '@/services/bi/reports'
import { existingRunId, useGenerateRun, useRuns } from '@/services/bi/runs'
import { toRows } from '@/services/cube/report'
import { useAuthStore } from '@/stores/auth-store'

import { buildRun, RunGenerationError, type RunFilterValues } from './generate'
import { RunFilterFields } from './RunFilterFields'
import { RunPdfButton } from './RunPdfButton'

const t = messages.documents.runs
const g = t.generate
const editorText = messages.documents.editor

function NewRunForm({
  id,
  template,
  onClose,
}: {
  id: string
  template: ReportDocument
  onClose: () => void
}) {
  const navigate = useNavigate()
  const meta = useCubeMeta()
  const generate = useGenerateRun(id)
  const periodDefault = template.filters.find((filter) => filter.type === 'period')?.default
  const [period, setPeriod] = useState(resolveRelativePeriod(String(periodDefault ?? '-1m')))
  const [filters, setFilters] = useState<RunFilterValues>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string>()

  async function submit() {
    if (!meta.data) return
    setBusy(true)
    setError(undefined)
    try {
      const views = new Map(
        meta.data.cubes.filter((cube) => cube.type === 'view').map((cube) => [cube.name, cube]),
      )
      const body = await buildRun({
        definition: template,
        views,
        period,
        filters,
        load: async (query) => toRows(await cubeApi.load(query)),
      })
      const run = await generate.mutateAsync(body)
      void navigate(`/belgeler/${id}/${run.id}`)
    } catch (failure) {
      const existing = existingRunId(failure)
      if (existing) {
        void navigate(`/belgeler/${id}/${existing}`)
      } else if (failure instanceof RunGenerationError) {
        setError(failure.message)
      } else if (failure instanceof ApiError && failure.status === 403) {
        setError(g.forbidden(''))
      } else {
        setError(g.failed)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="border-border flex flex-wrap items-end gap-3 rounded-md border p-4"
      onSubmit={(event) => {
        event.preventDefault()
        void submit()
      }}
    >
      <FilterField label={g.period} htmlFor="run-period">
        <MonthPicker id="run-period" value={period} onChange={setPeriod} />
      </FilterField>
      <RunFilterFields template={template} filters={filters} onChange={setFilters} />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy || !meta.data}>
          {busy ? g.submitting : g.submit}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          {g.cancel}
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-destructive w-full text-sm">
          {error}
        </p>
      )}
    </form>
  )
}

/**
 * Belge kopya listesi (`/belgeler/:id`): dönem, durum, üreten, tarih ve "Yeni dönem üret".
 * Üretim tarayıcıda yapılır (`generate.ts`); dönemin kopyası varsa mevcut kopya açılır.
 */
export function RunListPage() {
  const { id } = useParams()
  const report = useBiReport(id)
  const runs = useRuns(id)
  const identity = useAuthStore((state) => state.identity)
  const [creating, setCreating] = useState(false)

  const failure = report.error ?? runs.error
  if (failure) return <ErrorState error={failure} />
  if (!id || !report.data || !runs.data) return <Loading rows={4} />

  const template = reportDocumentSchema.safeParse(report.data.definition)
  if (!template.success) {
    return (
      <p role="alert" className="text-destructive text-sm">
        {editorText.invalidDefinition}
      </p>
    )
  }
  const items = runs.data.toSorted((a, b) => b.period.localeCompare(a.period))
  const creator = (userId: string | null) =>
    userId === null ? '—' : userId === identity?.id ? identity.full_name : userId.slice(0, 8)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 w-fit"
            render={<Link to="/belgeler" />}
            nativeButton={false}
          >
            <ArrowLeft />
            {editorText.back}
          </Button>
          <h1 className="text-lg font-semibold">
            {template.data.title} · {g.title}
          </h1>
          <p className="text-muted-foreground text-sm">{g.description}</p>
        </div>
        {!creating && (
          <Button onClick={() => setCreating(true)}>
            <FilePlus2 />
            {g.newRun}
          </Button>
        )}
      </div>

      {creating && (
        <NewRunForm id={id} template={template.data} onClose={() => setCreating(false)} />
      )}

      {items.length === 0 ? (
        <Empty title={g.empty} hint={g.emptyHint} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{g.columns.period}</TableHead>
              <TableHead>{g.columns.status}</TableHead>
              <TableHead>{g.columns.createdBy}</TableHead>
              <TableHead>{g.columns.createdAt}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((run) => (
              <TableRow key={run.id}>
                <TableCell className="font-medium">{periodLabel(run.period)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{t.status[run.status]}</Badge>
                </TableCell>
                <TableCell>{creator(run.created_by)}</TableCell>
                <TableCell>{formatDateTime(run.created_at)}</TableCell>
                <TableCell>
                  <div className="flex items-start justify-end gap-2">
                    <RunPdfButton reportId={id} runId={run.id} size="sm" />
                    <Button
                      size="sm"
                      variant="outline"
                      render={<Link to={`/belgeler/${id}/${run.id}`} />}
                      nativeButton={false}
                    >
                      {g.open}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
